import { DriverLocationPing } from '../../../graphql/requests/getDriverLocationTrail';
import {
  Delivery,
  GetOrderNode,
  GetOrders,
  PickUp,
  Redelivery,
  Route,
  RouteImportState,
  RoutingCohort,
  Warehouse,
} from '../../../graphql/types/delivery';
import { GDeliveryStatusDmp } from '../../../graphql/types/generated';

export enum PrimaryCard {
  RouteListCard = 'RouteListCard',
  RouteDetailsCard = 'RouteDetailsCard',
  StopDetailsCard = 'StopDetailsCard',
  OrderDetailsCard = 'OrderDetailsCard',
  CohortListCard = 'CohortListCard',
  CohortDetailsCard = 'CohortDetailsCard',
  BulkEditRouteCard = 'BulkEditRouteCard',
}

export enum SecondaryCard {
  RouteDetailsEditDriver = 'RouteDetailsEditDriver',
  RouteDetailsEditOrigin = 'RouteDetailsEditOrigin',
  RouteDetailsChangeRoute = 'RouteDetailsChangeRoute',
  StopPopoverChangeRoute = 'StopPopoverChangeRoute',
  RouteListEditRouteImport = 'RouteListEditRouteImport',
  ScenarioComparison = 'ScenarioComparison',
  StopDetailsEditDeliveredQuantity = 'StopDetailsEditDeliveredQuantity',
  EditLocationCohort = 'EditLocationCohort',
  UploadCsvRouteCard = 'UploadCsvRouteCard',
}

export type RoutePrimaryStatusState = {
  routeId: string;
  totalStops: number;
  totalStopsVisited: number;
  totalUndeliveredStops: number;
  totalPartiallyDeliveredStops: number;
  totalNotPickedUpStops: number;
  totalPartiallyPickedUpStops: number;
};

export type StopPrimaryStatusState = {
  routeId: string;
  stopId: string;
  stopStatus: GDeliveryStatusDmp;
};

export type RouteStatusState = {
  routeId: string;
  totalStops: number;
  totalStopsVisited: number;
  hasSecondaryStatuses: boolean;
  totalIncompleteTaskStops: number;
  totalUndeliveredStops: number;
  totalPartiallyDeliveredStops: number;
  totalNotPickedUpStops: number;
  totalPartiallyPickedUpStops: number;
  totalGoldMembers: number;
  totalSilverMembers: number;
  totalDoorDashStops: number;
  totalRefrigerationRequired: number;
  totalFreezerRequired: number;
  totalBienCuitStops: number;
  totalPastryStops: number;
  totalTrackedVendorStops: number;
  totalClosedStops: number;
  totalMetrobiStops: number;
  totalRefrigeratedDairyStops: number;
};

export type StopSecondaryStatus = {
  hasRepeatedIssues: boolean;
  hasIssues: boolean;
  hasIssueNotes: boolean;
  hasDeliveryNote: boolean;
  hasWarehouseNote: boolean;
  hasReminder: boolean;
  hasTasks: boolean;
  hasIncompleteTask: boolean;
  isOutsideDeliveryWindow: boolean;
  isFirstDelivery: boolean;
  isRefrigerationRequired: boolean;
  isFreezerRequired: boolean;
  hasBienCuitOrder: boolean;
  hasPastryOrder: boolean;
  hasTrackedVendorOrder: boolean;
  isClosed: boolean;
  hasMetrobiOrder: boolean;
  isMetrobiIneligible: boolean;
  hasRefrigeratedDairy: boolean;
};

export type StopStatusState = {
  routeId: string;
  stopId: string;
  stopStatus: GDeliveryStatusDmp;
  hasPastryOrders: boolean;
  stopSecondaryStatus: StopSecondaryStatus;
};

export type RoutingLocation = {
  locationId: string;
  value: boolean;
};

export type DeliveredQuantityEditor = {
  orderId?: string;
  orderLineId?: string;
  quantityDelivered?: number;
  itemName?: string;
  orderUnitQuantity?: number;
};

export enum RouteListTab {
  OdekoDeliveries = 'odeko-deliveries',
  // The Roadie sub-tab of Third Party. The value predates the merged tab and
  // the map's Roadie highlighting reads it.
  ThirdPartyDeliveries = 'third-party-deliveries',
  MetrobiDeliveries = 'metrobi-deliveries',
  BurqDeliveries = 'burq-deliveries',
  CustomerPickUps = 'customer-pick-ups',
}

// The Third Party tab's sub-tabs, in display order.
export const THIRD_PARTY_SUB_TABS: readonly RouteListTab[] = [
  RouteListTab.BurqDeliveries,
  RouteListTab.MetrobiDeliveries,
  RouteListTab.ThirdPartyDeliveries,
];

export const isThirdPartySubTab = (tab: RouteListTab): boolean =>
  THIRD_PARTY_SUB_TABS.includes(tab);

export type StopDetailsReturnTo = 'routeList';

// The stop-details tabs a caller can open on; the Drop-Off tab otherwise.
export type StopDetailsTab = 'deliveryDetails';

export enum Mode {
  'ViewRoutes',
  'ViewCohorts',
  'EditRoutes',
}

export type DeliveryState = {
  warehouses?: Warehouse[];
  warehousesLoading: boolean;
  routes?: Route[];
  routesLoading: boolean;
  routesError?: boolean;
  latestRoutesRequestId?: string;
  // Whether that request is a background poll (useProviderRoutePolling).
  latestRoutesRequestIsPoll?: boolean;
  latestStopStatusesRequestId?: string;
  latestPrimaryStatusesRequestId?: string;
  routesContext?: {
    warehouseId: string;
    nightEnd: string;
  };
  // When the routes on screen were read from the server (epoch ms), whichever
  // page loaded them; local edits leave it alone.
  routesLandedAt?: number;
  selectedWarehouseId?: string;
  primaryCard: {
    activeCard: PrimaryCard;
    contentVisible?: boolean;
  };
  secondaryCardVisible?: SecondaryCard;
  popover: {
    visible: boolean;
    stop?: Delivery | PickUp | Redelivery;
    order?: GetOrderNode;
  };
  nightEnd: {
    current?: string;
    viewing?: string;
  };
  selectedRouteId: {
    id: string | null;
    hover?: boolean;
  };
  selectedCohortId: {
    id: string | null;
    hover?: boolean;
  };
  routeDetailsEditor: {
    isSaving: boolean;
    driverName?: string;
    helperDriverName?: string;
  };
  changeRouteEditor: {
    selectedStops: (Delivery | Redelivery)[];
    isSaving: boolean;
  };
  locationCohortEditor: {
    businessName?: string;
    selectedLocationId?: string;
    routingCohort?: RoutingCohort;
  };
  deliveryStopsReorderLoading: boolean;
  stopDetails: {
    selectedStop?: Delivery | PickUp | Redelivery;
    // Where the card's back arrow goes when it is not the default route or
    // cohort details. Set only by the opener that needs it; every open assigns
    // it, so a later open from elsewhere never inherits it.
    returnTo?: StopDetailsReturnTo;
    // Assigned the same way: the tab this open starts on.
    initialTab?: StopDetailsTab;
  };
  createWarehouseDeliveryRoute: {
    isSaving: boolean;
  };
  routeImport: RouteImportState;
  manualRunRunning: boolean;
  manualRunTriggering: boolean;
  deliveryPrimaryStatuses: {
    routeStatuses: RoutePrimaryStatusState[];
    stopStatuses: StopPrimaryStatusState[];
    redeliveryStopStatuses: StopPrimaryStatusState[];
    loading: boolean;
    error?: boolean;
  };
  deliveryStatuses: {
    routeStatuses: RouteStatusState[];
    stopStatuses: StopStatusState[];
    redeliveryStopStatuses: StopStatusState[];
    loading: boolean;
    error?: boolean;
  };
  routingLocations: RoutingLocation[];
  deliveredQuantityEditor: DeliveredQuantityEditor;
  isRoutesSyncEnabled: boolean;
  selectedOrderId?: string;
  selectedRouteListTab: RouteListTab;
  // The sub-tab the GM last used, so re-entering Third Party lands there.
  lastThirdPartySubTab?: RouteListTab;
  thirdPartyOrders?: GetOrders;
  thirdPartyOrdersLoading: boolean;
  mode: Mode;
  mapIconFilters: {
    displayBienCuit: boolean;
    displayPastry: boolean;
    displayTrackedVendor: boolean;
    displayRefrigeratedDairy: boolean;
    displayDayNight: boolean;
    displayRefrigeration: boolean;
    displayMetrobiIneligible: boolean;
  };
  showDriverLocationTrail: boolean;
  driverLocationTrail: DriverLocationPing[];
  driverLocationTrailLoading: boolean;
};

export const initialState: DeliveryState = {
  warehouses: undefined,
  warehousesLoading: false,
  manualRunRunning: false,
  manualRunTriggering: false,
  routes: undefined,
  routesLoading: false,
  latestRoutesRequestId: undefined,
  routesContext: undefined,
  selectedWarehouseId: undefined,
  primaryCard: {
    activeCard: PrimaryCard.RouteListCard,
    contentVisible: undefined,
  },
  secondaryCardVisible: undefined,
  popover: {
    visible: false,
  },
  nightEnd: {
    current: undefined,
    viewing: undefined,
  },
  selectedRouteId: {
    id: null,
    hover: false,
  },
  selectedCohortId: {
    id: null,
    hover: false,
  },
  routeDetailsEditor: {
    isSaving: false,
    driverName: undefined,
    helperDriverName: undefined,
  },
  changeRouteEditor: {
    selectedStops: [],
    isSaving: false,
  },
  locationCohortEditor: {
    businessName: undefined,
    selectedLocationId: undefined,
    routingCohort: undefined,
  },
  deliveryStopsReorderLoading: false,
  stopDetails: {
    selectedStop: undefined,
  },
  createWarehouseDeliveryRoute: {
    isSaving: false,
  },
  routeImport: {
    isError: false,
    isLoading: false,
    isRunning: false,
    isUpdateTruckMutationLoading: false,
    plan: undefined,
    isPastWarehouseCutOffTime: false,
    scenarios: undefined,
    actualRoutesSummary: undefined,
    scenariosLoading: false,
    scenariosError: false,
  },
  deliveryPrimaryStatuses: {
    routeStatuses: [],
    stopStatuses: [],
    redeliveryStopStatuses: [],
    loading: false,
  },
  deliveryStatuses: {
    routeStatuses: [],
    stopStatuses: [],
    redeliveryStopStatuses: [],
    loading: false,
  },
  routingLocations: [],
  deliveredQuantityEditor: {
    orderId: undefined,
    orderLineId: undefined,
    quantityDelivered: undefined,
    itemName: undefined,
    orderUnitQuantity: undefined,
  },
  isRoutesSyncEnabled: false,
  selectedOrderId: undefined,
  selectedRouteListTab: RouteListTab.OdekoDeliveries,
  thirdPartyOrders: undefined,
  thirdPartyOrdersLoading: false,
  mode: Mode.ViewRoutes,
  mapIconFilters: {
    displayBienCuit: false,
    displayPastry: false,
    displayTrackedVendor: false,
    displayRefrigeratedDairy: false,
    displayDayNight: false,
    displayRefrigeration: false,
    displayMetrobiIneligible: false,
  },
  showDriverLocationTrail: false,
  driverLocationTrail: [],
  driverLocationTrailLoading: false,
};
